import React, { useState, useEffect } from "react";
import FullscreenImagesModal from "../CollectionItemPage/FullscreenImagesModal";
import { AssetHelper } from "../../ReactComponents/assetHelper";
import { Loader } from "../Ecommerce/Shared/Loader";

type CollectionImagesProps = {
    mediaId: string | null;
    id: string;
    title: string;
    ariaImageExpanded: string;
    mediaCopyright?: string;
    copyrightFallback?: string;
    fotographer?: string;
    fotographerLabel?: string;
};

export const CollectionImages = (props: CollectionImagesProps) => {
    const [imageInfoUrls, setImageInfoUrls] = useState<string[]>([]);
    const [mainImageUrl, setMainImageUrl] = useState<string>("");
    const [isMainImageLoading, setIsMainImageLoading] = useState<boolean>(true);
    const [showModal, setShowModal] = useState<boolean>(false);
    const [shouldRenderImage, setShouldRenderImage] = useState<boolean>(false);
    const [hasImage, setHasImage] = useState<boolean>(false);

    useEffect(() => {
        setHasImage(false);
        if (props.mediaId) {
            setIsMainImageLoading(true);
            setShouldRenderImage(false);
            requestManifestInfo();
        } else {
            setIsMainImageLoading(true);
            setShouldRenderImage(true);
        }
    }, [props.mediaId]);

    const requestManifestInfo = async () => {
        setIsMainImageLoading(true);
        const urlManifest = `https://iiif.micr.io/${props.mediaId}/manifest`;

        try {
            const response = await fetch(urlManifest, { credentials: "same-origin" });
            const result = await response.json();
            getimageUrlsFromManifestInfo(result);
        } catch (error) {
            console.log(error);
            setShouldRenderImage(true);
            setIsMainImageLoading(false);
        }
    };

    const getimageUrlsFromManifestInfo = (manifest: any) => {
        if (!manifest || !Array.isArray(manifest.items)) {
            setShouldRenderImage(true);
            setIsMainImageLoading(false);
            return;
        }

        const infoUrlarray: string[] = [];
        let firstServiceId: string | null = null;

        manifest.items.forEach((canvas: any) => {
            const pages = Array.isArray(canvas.items) ? canvas.items : [];
            pages.forEach((page: any) => {
                const annotations = Array.isArray(page.items) ? page.items : [];
                annotations.forEach((ann: any) => {
                    const body = Array.isArray(ann.body) ? ann.body[0] : ann.body;
                    if (!body) return;

                    const serviceEntry = Array.isArray(body.service) ? body.service[0] : body.service;
                    const serviceId: string | undefined = serviceEntry?.id || serviceEntry?.["@id"];
                    if (!serviceId) return;

                    if (!firstServiceId) firstServiceId = serviceId;

                    infoUrlarray.push(`${serviceId}/info.json`);
                });
            });
        });

        setImageInfoUrls(infoUrlarray);

        if (firstServiceId) {
            setMainImageUrl(`${firstServiceId}/full/max/0/default.jpg`);
        }

        setShouldRenderImage(true);
    };

    const handleOpenModal = () => {
        setShowModal(true);
    };

    const handleCloseModal = () => {
        setShowModal(false);
    };

    const isPlaceholderSrc = (src: string) =>
        src.includes(AssetHelper.images.placeholder) ||
        src.includes("/dist/assets/images/noImage.png");


    const imageOnLoadHandler = (event: React.SyntheticEvent<HTMLImageElement, Event>) => {
        setIsMainImageLoading(false);
        const img = event.currentTarget;
        setHasImage(!isPlaceholderSrc(img.src) && img.naturalWidth > 1);
    };

    const imageOnErrorHandler = (event: React.SyntheticEvent<HTMLImageElement, Event>) => {
        event.currentTarget.src = "/dist/assets/images/noImage.png";
        setIsMainImageLoading(false);
        setHasImage(false);
    };

    return (
        <div className="CollectionImages">
            <div className="CollectionImages__main-image">
                {isMainImageLoading && (
                    <div className="CollectionImages__main-image-loader">
                        <Loader />
                    </div>
                )}
                <figure style={{ display: isMainImageLoading ? "none" : "block" }}>
                    <button
                        className="CollectionImages__main-image-button"
                        onClick={handleOpenModal}
                        aria-label={props.ariaImageExpanded}
                        disabled={!props.mediaId}
                    >
                        {props.mediaId && (
                            <div aria-hidden={true} className="CollectionImages__expand-icon-container">
                                <div className="CollectionImages__expand-icon"></div>
                            </div>
                        )}
                        {shouldRenderImage && (
                            <img
                                src={
                                    !props.mediaId || mainImageUrl.length === 0
                                        ? AssetHelper.images.placeholder
                                        : mainImageUrl
                                }
                                onLoad={imageOnLoadHandler}
                                className="CollectionImages__thumbnail-image-main"
                                alt={props.title}
                                onError={imageOnErrorHandler}
                            />
                        )}
                    </button>
                    {(props.fotographer || props.mediaCopyright || props.copyrightFallback) && (
                        <figcaption className="CollectionImages__main-image-caption">
                            {props.fotographer
                                ? `${props.fotographerLabel ?? "Foto"}: ${props.fotographer}${props.mediaCopyright ? `, ${props.mediaCopyright}` : ""
                                }`
                                : props.mediaCopyright || props.copyrightFallback}
                        </figcaption>
                    )}
                </figure>
            </div>

            {showModal && (
                <FullscreenImagesModal
                    selectedIndex={0}
                    imageInfoUrls={imageInfoUrls}
                    handleClose={handleCloseModal}
                    show={showModal}
                />
            )}
        </div>
    );
};
